import createAuth0Client, {
  Auth0ClientOptions,
  GetTokenWithPopupOptions,
  PopupConfigOptions,
  PopupLoginOptions,
  RedirectLoginOptions,
} from "@auth0/auth0-spa-js";
import React, { useContext, useEffect, useState } from "react";

interface ContextValueType {
  isAuthenticated?: boolean;
  user?: any;
  loading?: boolean;
  popupOpen?: boolean;
  handleRedirectCallback?: () => void;
  getIdTokenClaims?: (...p: any) => any;
  loginWithRedirect?: (options?: RedirectLoginOptions) => void;
  loginWithPopup?: (options?: PopupLoginOptions) => void;
  getTokenSilently?: (...p: any) => Promise<string>;
  getTokenWithPopup?: (
    options?: GetTokenWithPopupOptions,
    config?: PopupConfigOptions
  ) => Promise<string>;
  logout?: (...p: any) => any;
}

interface IProps extends Auth0ClientOptions {
  children: any;
  onRedirectCallback: (appState: any) => void;
}

export const Auth0Context = React.createContext<ContextValueType>({});
export const useAuth0: any = () => useContext(Auth0Context);
export const Auth0Provider: React.FC<IProps> = ({
  children,
  onRedirectCallback,
  ...initOptions
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | undefined>();
  const [user, setUser] = useState<any>();
  const [auth0Client, setAuth0] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);

  useEffect(() => {
    const initAuth0 = async () => {
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (
        window.location.search.includes("code=") &&
        window.location.search.includes("state=")
      ) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }

      setIsAuthenticated(await auth0FromHook.isAuthenticated());

      if (await auth0FromHook.isAuthenticated()) {
        setUser(await auth0FromHook.getUser());
      }

      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);
    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }
    setUser(await auth0Client.getUser());
    setIsAuthenticated(true);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(await auth0Client.getUser());
  };

  const loginWithRedirect = (options?: RedirectLoginOptions) => {
    window.localStorage.setItem(
      "redirectTo",
      `${window.location.pathname}${window.location.search}`
    );
    auth0Client.loginWithRedirect(options);
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        popupOpen,
        loginWithPopup,
        loginWithRedirect,
        handleRedirectCallback,
        getIdTokenClaims: (...p: any) => auth0Client?.getIdTokenClaims(...p),
        getTokenSilently: (...p: any) => auth0Client?.getTokenSilently(...p),
        getTokenWithPopup: (...p: any) => auth0Client?.getTokenWithPopup(...p),
        logout: (...p: any) => auth0Client?.logout(...p),
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
